import {closest} from './wpstg-polyfill';
import WPStagingCommon from '../wpstg.js';

/**
 * WP STAGING basic jQuery replacement
 */

/**
 * Shortcut for document.querySelector() or jQuery's $()
 * Return single element only
 */
export function qs(selector) {
  return document.querySelector(selector);
}

/**
 * Shortcut for document.querySelector() or jQuery's $()
 * Return collection of elements
 */
export function all(selector) {
  return document.querySelectorAll(selector);
}

/**
 * alternative of jQuery - $(parent).on(event, selector, handler)
 */
export function addEvent(parent, evt, selector, handler) {
  if (!parent instanceof Element) {
    return;
  }

  parent.addEventListener(evt, function(event) {
    if (event.target.matches(selector + ', ' + selector + ' *')) {
      handler(event.target.closest(selector), event);
    }
  }, false);
}

export function slideDown(element, duration = 400) {
  element.style.display = 'block';
  element.style.overflow = 'hidden';
  const height = element.offsetHeight;
  element.style.height = '0px';
  element.style.transitionProperty = 'height';
  element.style.transitionDuration = duration + 'ms';
  setTimeout(() => {
    element.style.height = height + 'px';
    window.setTimeout(() => {
      element.style.removeProperty('height');
      element.style.removeProperty('overflow');
      element.style.removeProperty('transition-duration');
      element.style.removeProperty('transition-property');
    }, duration);
  }, 0);
}

export function slideUp(element, duration = 400) {
  element.style.display = 'block';
  element.style.overflow = 'hidden';
  const height = element.offsetHeight;
  element.style.height = height + 'px';
  element.style.transitionProperty = 'height';
  element.style.transitionDuration = duration + 'ms';
  setTimeout(() => {
    element.style.height = '0px';
    window.setTimeout(() => {
      element.style.display = 'none';
      element.style.removeProperty('height');
      element.style.removeProperty('overflow');
      element.style.removeProperty('transition-duration');
      element.style.removeProperty('transition-property');
    }, duration);
  }, 0);
}

export function fadeOut(element, duration = 300) {
  element.style.opacity = 1;
  element.style.transitionProperty = 'opacity';
  element.style.transitionDuration = duration + 'ms';
  setTimeout(() => {
    element.style.opacity = 0;
    window.setTimeout(() => {
      element.style.display = 'none';
      element.style.removeProperty('opacity');
      element.style.removeProperty('transition-duration');
      element.style.removeProperty('transition-property');
    }, duration);
  }, 0);
}

export function getNextSibling(element, selector) {
  let sibling = element.nextElementSibling;

  while (sibling) {
    if (sibling.matches(selector)) {
      return sibling;
    }

    sibling = sibling.nextElementSibling;
  }
};

export function getPreviousSibling(element, selector) {
  let sibling = element.previousElementSibling;

  while (sibling) {
    if (sibling.matches(selector)) {
      return sibling;
    }

    sibling = sibling.previousElementSibling;
  }
}

export function getParents(element, selector) {
  const result = [];
  for (let parent = element && element.parentElement; parent; parent = parent.parentElement) {
    if (parent.matches(selector)) {
      result.push(parent);
    }
  }

  return result;
}

/**
 * Check if element has class name
 * @param element
 * @param className
 * @return {boolean}
 */
export function hasClass(element, className) {
  return (' ' + element.className + ' ').indexOf(' ' + className+ ' ') > -1;
}

/**
 * Dispatches a change on an element that will trigger, depending on the element type,
 * cascading changes on elements dependant on the one that triggered the change and that
 * belong in the same container.
 *
 * @param {Element} element A reference to the Element the change was triggered from.
 *
 * @return {void} The function does not return any value and will have the side-effect of
 *                hiding or showing dependant elements.
 */
export function handleDisplayDependencies(element) {
  if (!element instanceof Element || !element.id) {
    return;
  }

  const containerSelector = '.wpstg-container';
  // Use the default WordPress CSS class to hide and show the objects.
  const hiddenClass = 'hidden';
  const elementType = element.getAttribute('type');

  switch (elementType) {
    case 'checkbox':
      // Go as high as the container that contains this element.
      const container = closest(element, containerSelector);

      if (container === null) {
        return;
      }

      const showIfChecked = container.querySelectorAll(`[data-show-if-checked="${element.id}"]`);
      const showIfUnchecked = container.querySelectorAll(`[data-show-if-unchecked="${element.id}"]`);
      const checked = element.checked;

      if (showIfChecked.length) {
        for (const el of showIfChecked) {
          if (checked) {
            el.classList.remove(hiddenClass);
          } else {
            el.classList.add(hiddenClass);
          }
        }
      }

      if (showIfUnchecked.length) {
        for (const el of showIfUnchecked) {
          if (checked) {
            el.classList.add(hiddenClass);
          } else {
            el.classList.remove(hiddenClass);
          }
        }
      }

      return;
    default:
      // Not a type we handle.
      return;
  }
}

/**
 * Initializes the display status of any element that depends on the status of another to
 * either hide or show.
 *
 * @return {void} The function will have the side-effect to toggle the hide/show class from
 *                elements dependant on another to hide or show.
 */
export function initDependantDisplay() {
  const displayControllerSelector = '[onchange^="WPStaging.handleDisplayDependencies"]';
  const displayControllers = document.querySelectorAll(displayControllerSelector);
  for (const displayController of displayControllers) {
    handleDisplayDependencies(displayController);
  }
}

/**
 * Toggle target element set in data-wpstg-target of the given element
 *
 * @param {Element} element A reference to the Element the change was triggered from.
 *
 * @return {void} The function does not return any value and will have the side-effect of
 *                hiding or showing dependant elements.
 */
export function handleToggleElement(element) {
  if (!element instanceof Element || !element.getAttribute('data-wpstg-target')) {
    return;
  }

  const containerSelector = '.wpstg_admin';
  // Use the default WordPress CSS class to hide and show the objects.
  const hiddenClass = 'hidden';

  // Go as high as the container that contains this element.
  const container = closest(element, containerSelector);

  if (container === null) {
    return;
  }

  const elements = container.querySelectorAll(element.getAttribute('data-wpstg-target'));

  if (elements.length) {
    for (const el of elements) {
      el.classList.toggle(hiddenClass);
    }
  }
}

/**
 * Copy text in data-wpstg-copy to element(s) in data-wpstg-target
 *
 * @param {Element} element
 *
 * @return {void}
 */
export function handleCopyPaste(element) {
  if (!element instanceof Element || !element.getAttribute('data-wpstg-target') || !element.getAttribute('data-wpstg-copy')) {
    return;
  }

  const containerSelector = '.wpstg_admin';

  // Go as high as the container that contains this element.
  const container = closest(element, containerSelector);

  if (container === null) {
    return;
  }

  navigator.clipboard.writeText(element.getAttribute('data-wpstg-copy'));
  const elements = container.querySelectorAll(element.getAttribute('data-wpstg-target'));

  if (elements.length) {
    for (const el of elements) {
      el.value = element.getAttribute('data-wpstg-copy', '');
    }
  }
}

/**
 * Copy text in data-wpstg-source to clipboard
 *
 * @param {Element} element
 *
 * @return {void}
 */
export function handleCopyToClipboard(element) {
  if (!element instanceof Element || !element.getAttribute('data-wpstg-source')) {
    return;
  }

  const containerSelector = '.wpstg_admin';

  // Go as high as the container that contains this element.
  const container = closest(element, containerSelector);

  if (container === null) {
    return;
  }

  const el = container.querySelector(element.getAttribute('data-wpstg-source'));

  if (el) {
    navigator.clipboard.writeText(el.value);
  }
}

/**
 * A confirmation modal
 *
 * @param title
 * @param html
 * @param confirmText
 * @param confirmButtonClass
 * @return Promise
 */
export function confirmModal(title, html, confirmText, confirmButtonClass) {
  return WPStagingCommon.getSwalModal(false, {
    container: 'wpstg-swal-push-container',
    confirmButton: confirmButtonClass + ' wpstg--btn--confirm wpstg-blue-primary wpstg-button wpstg-link-btn',
  }).fire({
    title: title,
    icon: 'warning',
    html: html,
    width: '750px',
    focusConfirm: false,
    confirmButtonText: confirmText,
    showCancelButton: true,
  });
};
/**
 * @param className
 * @param callback
 */
export function setClickHandlerByClass(className, callback) {
  const elements = document.querySelectorAll(`.${className}`);

  if (elements.length) {
    elements.forEach((element) => {
      element.addEventListener('click', callback);
    });
  } else {
    console.warn(`No elements found with class: ${className}`);
  }
};

/**
 * Initializes a module using a MutationObserver.
 *
 * @param {string} selector - The CSS selector for the target element.
 * @param {function} ModuleConstructor - The constructor function of the module to be initialized.
 *
 * @todo Unused, consider removing this in the future
 */
export function initializeModuleWithObserver(selector, ModuleConstructor) {
  const observer = new MutationObserver(function(mutationsList, observer) {
    const targetElement = document.querySelector(selector);
    if (targetElement !== null) {
      new ModuleConstructor();
      observer.disconnect();
    }
  });
  observer.observe(document.documentElement, {childList: true, subtree: true});
};

/**
 * Checks if a modal specified by the given selector is available in the DOM.
 * If the modal is available, it waits for a delay and then initializes the Module.
 * If the modal is not yet available, it recursively checks after a brief interval.
 *
 * @param {string} selector - CSS selector for the element to search for.
 * @param {function} Module - Constructor function to create an instance of the module.
 *
 * @todo Unused, consider removing this in the future
 */
export function initializeModuleDelayedIfElementIsAvailable(selector, Module) {
  const element = document.querySelector(selector);
  if (element) {
    setTimeout(() => {
      new Module();
    }, 1000);
  } else {
    setTimeout(() => initializeModuleDelayedIfElementIsAvailable(selector, Module), 10);
  }
};

/**
 * Hides elements in the DOM that match the given selector by setting their display style to 'none'.
 *
 * @param {string} selector - CSS selector for the elements to be hidden.
 * @return {void}
 */
export function hide(selector) {
  const elements = document.querySelectorAll(selector);
  elements.forEach((element) => {
    element.style.display = 'none';
  });
};

/**
 * Displays elements in the DOM that match the given selector by setting their display style to 'block'.
 *
 * @param {string} selector - CSS selector for the elements to be displayed.
 * @return {void}
 */
export function show(selector) {
  const elements = document.querySelectorAll(selector);
  elements.forEach((element) => {
    element.style.display = 'block';
  });
}

/**
 *
 * @param e
 */
export function copyTextToClipboard(e) {
  const dataCopyContent = e.target.getAttribute('data-copy-content');
  if (dataCopyContent) {
    navigator.clipboard.writeText(dataCopyContent).then(() => {
      notify('success', 'Copied to clipboard');
    }).catch((err) => {
      notify('error', 'Unable to copy to clipboard', err);
    });
  }
}

/**
 * Displays a notification message
 *
 * @param type
 * @param message
 */
export function notify(type, message) {
  const notyf = new Notyf({
    duration: 6000,
    position: {
      x: 'center',
      y: 'bottom',
    },
    dismissible: true,
    types: [
      {
        type: 'warning',
        background: 'orange',
        icon: true,
      },
    ],
  });
  notyf.open({type: type, message: message});
}

/**
 * Checks if an element is null, undefined, or empty (for strings or arrays)
 *
 * @param element
 * @return {boolean}
 */
export function checkIfElementNullOrUndefinedOrEmpty(element) {
  if (element === null || element === undefined) {
    return true;
  }

  if (typeof element === 'string' && element.trim() === '') {
    return true;
  }

  if (Array.isArray(element) && element.length === 0) {
    return true;
  }

  return false;
}

/**
 * @param visibility
 * @return {void}
 */
export function loadingBar(visibility = 'visible') {
  const loader = document.querySelectorAll('.wpstg-loading-bar-container');
  loader.forEach(function(element) {
    if (element) {
      element.style.visibility = visibility;
    }
  });
}

export function isElementChecked(selector) {
  const element = qs(selector);
  if (!element) {
    return false;
  }

  return element.checked;
}

export function getInputValue(selector, defaultValue = '') {
  const element = qs(selector);
  if (!element) {
    return defaultValue;
  }

  return element.value;
}

/**
 * @param visibility
 * @return {void}
 */
export function loadingPlaceholder(visibility = 'visible') {
  const loader = all('.wpstg-loading-placeholder-container');
  loader.forEach(function(element) {
    if (element) {
      element.style.visibility = visibility;
    }
  });

  setTimeout(() => {
    loader.forEach(function(element) {
      if (element && element.style.visibility === 'visible') {
        element.style.visibility = 'hidden';
      }
    });
  }, 5000);

  const container = qs('.wpstg--tab--contents');

  if (visibility === 'visible') {
    loadingBar();
    container && (container.style.overflow = 'hidden');
    return;
  }

  loadingBar('hidden');
  container && (container.style.overflow = 'unset');
}

/**
 * @return {string|null}
 */
export function getActivePage() {
  return (window.location.search.match(/[\?&]page=([^&]*)/) || [])[1] || null;
}

/**
 *
 * @param container
 * @return {Promise<void>}
 */
export async function setLoadingPlaceholder(container = '') {
  if (container === '') {
    container = qs('.wpstg--tab--contents');
  }

  if (container === null) {
    return;
  }

  const loadingPlaceholderContainer = container.querySelector('.wpstg-loading-placeholder-container');
  const loadingWrapper = loadingPlaceholderContainer.querySelector('.wpstg-dom-loading-wrapper');

  if (!loadingWrapper) {
    return;
  }

  if (loadingWrapper.offsetHeight !== container.offsetHeight) {
    const loadingLines = loadingWrapper.querySelectorAll('.wpstg-loading-lines-bar');
    loadingLines.forEach((line) => line.remove());

    loadingWrapper.querySelector('.wpstg-loading-line')?.remove();
    loadingWrapper.querySelector('.wpstg-loading-last-line-bar')?.remove();
    do {
      loadingWrapper.insertAdjacentHTML('beforeend', '<div class="wpstg-loading-lines-bar"></div>');
      await new Promise((resolve) => setTimeout(resolve, 0));
    } while (loadingWrapper.offsetHeight <= (container.offsetHeight -40));

    loadingWrapper.firstElementChild?.classList.add('wpstg-loading-first-line-bar');
    loadingWrapper.lastElementChild?.classList.add('wpstg-loading-last-line-bar');
  }

  await new Promise((resolve) => setTimeout(resolve, 0));
}

/**
 * Normalizes a URL path and appends a slug to it, ensuring there are no consecutive slashes.
 * @param path {string}
 * @param slug {string}
 * @return {string}
 */
export function normalizeAndAppendSlug(path, slug) {
  let normalizedPath = path.replace(/\/+$/g, '');
  normalizedPath += '/' + slug + '/';
  return normalizedPath.replace(/\/+/g, '/');
}
